import { useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Dialog } from "@/components/common/Dialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { FilterChip } from "@/components/design/FilterChip";
import { Avatar } from "@/components/common/Avatar";
import { Select } from "@/components/design/Select";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { SORT_OPTIONS, type SortDir } from "@/lib/collection-filter";

export type GroupSelection = string;

export interface SaveGroupLite {
  id: string;
  name: string;
  itemCount: number;
}

export function SaveCollectionPage({
  title,
  description,
  dataGraphql,
  showBackButton = false,
  sidebar,
  children,
}: {
  title: string;
  description: ReactNode;
  dataGraphql?: string;
  showBackButton?: boolean;
  sidebar: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10" data-graphql={dataGraphql}>
      {showBackButton ? (
        <div className="mb-5">
          <BackButton />
        </div>
      ) : null}
      <BoxHeader title={title} variant="hero" />
      <div className="field-meta leading-snug text-ink/80">{description}</div>

      <div className="mt-5 flex flex-col gap-8 @lg:flex-row">
        {sidebar}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

export function SaveCollectionControls({
  statusLabel,
  loading,
  errorMessage,
  search,
  sort,
  ungroupedLabel,
  ungroupedActive,
  showUngroupedToggle,
  onSearchChange,
  onSortChange,
  onUngroupedToggle,
}: {
  statusLabel: string;
  loading: boolean;
  errorMessage?: string;
  search: string;
  sort: SortDir;
  ungroupedLabel: string;
  ungroupedActive: boolean;
  showUngroupedToggle: boolean;
  onSearchChange: (next: string) => void;
  onSortChange: (next: SortDir) => void;
  onUngroupedToggle: () => void;
}) {
  const intl = useIntl();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="min-w-[7rem] text-[12px] text-ink/55">
        {loading ? (
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        ) : errorMessage ? (
          <FormattedMessage
            id="save.collection.errorPrefix"
            defaultMessage="Error: {message}"
            values={{ message: errorMessage }}
          />
        ) : (
          statusLabel
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={intl.formatMessage({ id: "save.collection.searchPlaceholder", defaultMessage: "Search…" })}
          className="w-40 rounded-full bg-page px-3 py-1.5 text-[12px] text-ink ring-1 ring-ink/10 transition placeholder:text-ink/40 focus:outline-none focus:ring-ink/30 [&::-webkit-search-cancel-button]:appearance-none"
        />
        <Select
          value={sort}
          onChange={(v) => onSortChange(v as SortDir)}
          options={SORT_OPTIONS.map((o) => ({ value: o.value, label: intl.formatMessage({ id: o.labelId }) }))}
          className="w-32 !rounded-full !bg-page !py-1.5 !text-[12px]"
        />
      </div>
      {showUngroupedToggle ? (
        <div className="flex items-center gap-2">
          <FilterChip
            active={!ungroupedActive}
            onClick={() => {
              if (ungroupedActive) onUngroupedToggle();
            }}
          >
            <FormattedMessage id="save.collection.allLabel" defaultMessage="Alle" />
          </FilterChip>
          <FilterChip
            active={ungroupedActive}
            onClick={() => {
              if (!ungroupedActive) onUngroupedToggle();
            }}
          >
            {ungroupedLabel}
          </FilterChip>
        </div>
      ) : null}
    </div>
  );
}

export function SaveCollectionEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md bg-ink/[0.02] px-4 py-8 text-center text-[13px] text-ink/55 ring-1 ring-ink/15">
      {children}
    </div>
  );
}

export function SaveCollectionGrid({
  variant = "person",
  children,
}: {
  variant?: "person" | "shared";
  children: ReactNode;
}) {
  return (
    <div
      className={`grid grid-cols-1 gap-3 @sm:grid-cols-2 ${
        variant === "person" ? "@xl:grid-cols-3" : "@lg:grid-cols-3 @xl:grid-cols-4"
      }`}
    >
      {children}
    </div>
  );
}

export function SaveCollectionSidebar({
  topControls,
  groups,
  activeGroup,
  allLabel,
  allCount,
  addLabel,
  renameLabel,
  deleteLabel,
  onGroupChange,
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
  dropTargetId,
  dropHandlers,
}: {
  topControls?: ReactNode;
  groups: SaveGroupLite[];
  activeGroup: GroupSelection;
  allLabel?: string;
  allCount?: number;
  addLabel: string;
  renameLabel?: string;
  deleteLabel?: string;
  onGroupChange: (g: GroupSelection) => void;
  onCreateGroup: () => void;
  onRenameGroup: (group: SaveGroupLite) => void;
  onDeleteGroup: (group: SaveGroupLite) => void;
  dropTargetId?: string | null;
  dropHandlers?: (groupId: string) => {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
}) {
  const intl = useIntl();
  const resolvedAllLabel =
    allLabel ?? intl.formatMessage({ id: "save.collection.allGroups", defaultMessage: "All" });
  const resolvedRenameLabel =
    renameLabel ??
    intl.formatMessage({ id: "save.collection.renameGroup", defaultMessage: "Rename group" });
  const resolvedDeleteLabel =
    deleteLabel ??
    intl.formatMessage({ id: "save.collection.deleteGroup", defaultMessage: "Delete group" });
  return (
    <aside className="w-full @lg:w-60 @lg:shrink-0">
      {topControls ? <div>{topControls}</div> : null}

      <nav className={topControls ? "mt-4 space-y-0.5" : "space-y-0.5"}>
        <SaveGroupRow
          label={resolvedAllLabel}
          count={allCount}
          active={activeGroup === "all"}
          isDropTarget={dropTargetId === "all"}
          onClick={() => onGroupChange("all")}
          {...dropHandlers?.("all")}
        />

        {groups.length > 0 ? <div className="my-2 border-t border-ink/10" /> : null}

        {groups.map((g) => (
          <SaveGroupRow
            key={g.id}
            label={g.name}
            count={g.itemCount}
            active={activeGroup === g.id}
            isDropTarget={dropTargetId === g.id}
            onClick={() => onGroupChange(g.id)}
            onRename={() => onRenameGroup(g)}
            onDelete={() => onDeleteGroup(g)}
            renameLabel={resolvedRenameLabel}
            deleteLabel={resolvedDeleteLabel}
            {...dropHandlers?.(g.id)}
          />
        ))}
      </nav>

      <button
        type="button"
        onClick={onCreateGroup}
        data-testid="save-group-add"
        className="mt-4 w-full rounded-md px-3 py-2 text-left text-[12px] text-ink/70 ring-1 ring-ink/15 transition hover:bg-ink/[0.02] hover:text-ink"
      >
        {addLabel}
      </button>
    </aside>
  );
}

function SaveGroupRow({
  label,
  count,
  active,
  isDropTarget,
  onClick,
  onRename,
  onDelete,
  renameLabel,
  deleteLabel,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  label: string;
  count?: number;
  active: boolean;
  isDropTarget?: boolean;
  onClick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  renameLabel?: string;
  deleteLabel?: string;
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}) {
  const intl = useIntl();
  return (
    <div
      data-testid="save-group-row"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`group flex items-center gap-1 rounded-md pr-1 transition ${
        isDropTarget
          ? "ring-2 ring-accent"
          : active
            ? "bg-ink/[0.06]"
            : "hover:bg-ink/[0.06]"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className={`flex flex-1 items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px] ${
          active ? "font-medium text-ink" : "text-ink/70"
        }`}
      >
        <span className="truncate">{label}</span>
        {typeof count === "number" ? (
          <span className="shrink-0 text-[11px] tabular-nums text-ink/40">{count}</span>
        ) : null}
      </button>
      {onRename || onDelete ? (
        <span className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
          {onRename ? (
            <button
              type="button"
              onClick={onRename}
              data-testid="save-group-rename"
              title={intl.formatMessage({ id: "save.collection.rename", defaultMessage: "Rename" })}
              aria-label={
                renameLabel ??
                intl.formatMessage({ id: "save.collection.renameGroup", defaultMessage: "Rename group" })
              }
              className="rounded p-1 text-ink/45 hover:text-ink"
            >
              <ShareIcon name="pencil" className="h-3.5 w-3.5" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              data-testid="save-group-delete"
              title={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
              aria-label={
                deleteLabel ??
                intl.formatMessage({ id: "save.collection.deleteGroup", defaultMessage: "Delete group" })
              }
              className="rounded p-1 text-ink/45 hover:text-accent"
            >
              <ShareIcon name="trash" className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function SaveGroupDeleteConfirm({
  open,
  title,
  message,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  return (
    <ConfirmDialog
      open={open}
      title={title}
      message={message}
      confirmLabel={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
      cancelLabel={intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
      confirmTestId="save-group-delete-confirm"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

export function SaveGroupNameDialog({
  open,
  title,
  initial,
  placeholder,
  saving,
  issue,
  submitLabel,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  initial: string;
  placeholder: string;
  saving?: boolean;
  issue?: string | null;
  submitLabel?: string;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const intl = useIntl();
  const [name, setName] = useState(initial);

  // Dialog keeps its children mounted across open/close, so reset the field to
  // `initial` each time it (re)opens by tracking the prior open/initial pair.
  const [seed, setSeed] = useState({ open, initial });
  if (seed.open !== open || seed.initial !== initial) {
    setSeed({ open, initial });
    if (open) setName(initial);
  }

  const commit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = String(new FormData(e.currentTarget).get("name") ?? "").trim();
    if (name && !saving) onSave(name);
  };

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form onSubmit={commit} className="space-y-5">
        <div>
          <div className="field-label mb-1 text-ink/55">
            <FormattedMessage id="save.collection.nameLabel" defaultMessage="Name" />
          </div>
          <input
            autoFocus
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            data-testid="save-group-name-input"
            className="field-value w-full rounded bg-surface px-2 py-1 text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-1 focus:ring-ink/30"
          />
        </div>

        {issue ? <p data-testid="save-group-name-issue" className="text-sm text-accent">{issue}</p> : null}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !name.trim()}
            data-testid="save-group-name-save"
            className={`inline-flex items-center gap-1.5 px-5 py-2 ${
              saving || !name.trim() ? "cursor-not-allowed opacity-50" : ""
            }`}
          >
            {saving ? <SaveInlineSpinner /> : null}
            {saving ? (
              <FormattedMessage id="common.saving" defaultMessage="Saving…" />
            ) : (
              submitLabel ?? intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function SaveGroupPopover({
  triggerLabel,
  options,
  memberIds,
  pendingId,
  glass = false,
  onToggle,
}: {
  triggerLabel: string;
  options: SaveGroupLite[];
  memberIds: Set<string>;
  pendingId: string | null;
  glass?: boolean;
  onToggle: (groupId: string, isMember: boolean) => void;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  if (options.length === 0) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="save-group-popover-trigger"
        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] text-ink/70 transition ${
          glass
            ? "bg-white/30 shadow-sm ring-1 ring-white/30 backdrop-blur-md hover:bg-surface hover:text-ink"
            : "bg-ink/[0.06] hover:bg-ink/10 hover:text-ink"
        }`}
      >
        {triggerLabel}
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-30 mt-1 flex w-56 max-w-[240px] flex-wrap gap-1 rounded-md bg-surface p-2 shadow-lg ring-1 ring-ink/10">
            {options.map((g) => {
              const isMember = memberIds.has(g.id);
              const pending = pendingId === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  disabled={pendingId !== null}
                  onClick={() => onToggle(g.id, isMember)}
                  data-testid="save-group-popover-option"
                  title={
                    isMember
                      ? intl.formatMessage(
                          {
                            id: "save.collection.removeFromGroup",
                            defaultMessage: "Remove from „{name}”",
                          },
                          { name: g.name },
                        )
                      : intl.formatMessage(
                          {
                            id: "save.collection.addToGroup",
                            defaultMessage: "Add to „{name}”",
                          },
                          { name: g.name },
                        )
                  }
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] transition disabled:opacity-60 ${
                    isMember
                      ? "bg-accent/15 text-accent hover:bg-red-100 hover:text-red-600"
                      : "bg-ink/[0.06] text-ink/80 hover:bg-accent/15 hover:text-accent"
                  }`}
                >
                  {pending ? <SaveInlineSpinner /> : isMember ? "✓ " : null}
                  {g.name}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function SaveRemoveItemDialog({
  open,
  title,
  message,
  secondaryActionLabel,
  destructiveActionLabel,
  onSecondaryAction,
  onDestructiveAction,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  secondaryActionLabel?: string;
  destructiveActionLabel: string;
  onSecondaryAction?: () => void;
  onDestructiveAction: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title} maxWidth="md">
      <div className="mb-6 text-sm leading-relaxed text-ink/70">{message}</div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
          className="px-4 py-1.5 text-[12px]"
        >
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        {secondaryActionLabel && onSecondaryAction ? (
          <Button
            variant="secondary"
            onClick={onSecondaryAction}
            data-testid="remove-item-secondary"
            className="px-4 py-1.5 text-[12px]"
          >
            {secondaryActionLabel}
          </Button>
        ) : null}
        <Button
          variant="primary"
          onClick={onDestructiveAction}
          data-testid="remove-item-delete"
          className="px-4 py-1.5 text-[12px]"
        >
          {destructiveActionLabel}
        </Button>
      </div>
    </Dialog>
  );
}

export function SavePersonCollectionCard({
  title,
  subtitle,
  avatarContent,
  avatarMime,
  draggable,
  onDragStart,
  onDragEnd,
  openHref,
  onRemove,
  removeLabel,
  actions,
}: {
  id: string;
  title: string;
  subtitle?: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  openHref?: string;
  onRemove: () => void;
  removeLabel: string;
  actions: ReactNode;
}) {
  const intl = useIntl();

  return (
    <div
      draggable={draggable}
      data-testid="save-collection-card"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`box relative flex items-center gap-3 bg-ink/[0.06] p-3 transition hover:ring-accent/40 ${
        openHref ? "cursor-pointer" : ""
      } ${draggable ? "active:cursor-grabbing" : ""}`}
    >
      {openHref ? (
        // Stretched link: a real anchor covering the whole card so middle-click,
        // Cmd/Ctrl-click and "open in new tab" work natively, while a plain
        // left-click still routes through the SPA router. draggable={false} lets
        // the drag gesture fall through to the draggable card container.
        <Link
          to={openHref}
          draggable={false}
          aria-label={intl.formatMessage(
            { id: "save.collection.openItem", defaultMessage: "Open {title}" },
            { title },
          )}
          title={intl.formatMessage(
            { id: "save.collection.openItem", defaultMessage: "Open {title}" },
            { title },
          )}
          className="absolute inset-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        />
      ) : null}
      <Avatar person={{ name: title, avatarContent, avatarMime }} size="lg" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium leading-snug">{title}</div>
        {subtitle ? <div className="truncate text-[11px] text-ink/55">{subtitle}</div> : null}
      </div>

      <div className="relative z-10 flex shrink-0 items-center gap-1.5">
        {actions}
        <button
          type="button"
          onClick={onRemove}
          data-testid="save-collection-card-remove"
          title={removeLabel}
          aria-label={removeLabel}
          className="rounded-full bg-ink/[0.06] p-1.5 text-ink/45 transition hover:bg-ink/10 hover:text-accent"
        >
          <ShareIcon name="trash" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function SaveInlineSpinner({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      aria-hidden
    />
  );
}

